import React, { useEffect } from 'react'
import {
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress
} from '@material-ui/core'
import { actions } from '../../listView.reducer'
import {
  ListViewDispatchContext,
  ListViewStateContext
} from '../../listView.context'
import {
  prepTemplateForSending,
  emailStages,
  getInitialState,
  convertLocalTemplateToDBSchema,
  getSupervisorFromId
} from './email'
import {
  validators,
  getScheduledTime,
  getEmailTemplates,
  getTemplateType
} from './helper'
import { massProcessingApi } from '@atheneum/api'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { stores } from '../../../stores'
import getAdditionalReplacements from '../utils/replacements'

export const survey = {
  name: 'survey',
  title: 'Run survey',
  loadStage: 4,
  component: SurveyActionComponent,
  stages: [
    emailStages[0],
    emailStages[1],
    {
      validators: [validators.email],
      full: false,
      component: SurveySelector
    },
    emailStages[2],
    {
      validators: [validators.email],
      component: StatusPage,
      full: false
    }
  ],
  finishAction: sendSurveys
}

function StatusPage() {
  const MPState = React.useContext(ListViewStateContext)
  const dispatch = React.useContext(ListViewDispatchContext)
  return (
    <div style={{ marginLeft: '10px' }}>
      Survey in progress
      <Button
        onClick={() => {
          dispatch({ type: actions.END_ACTION })
          massProcessingApi.stopMassAction(MPState.massAction.id)
        }}>
        Stop
      </Button>
    </div>
  )
}
/**
 * Send surveys
 *
 * @param { ReturnType<getInitialState> & { surveyPath: string; UUIDPrefix: string; UIDParameter: string; baseLink: string; surveyType: string;  } } state
 */
async function sendSurveys(dispatch, { listId, items }, state) {
  try {
    const templateType = getTemplateType(state)
    // If it's not automated followup, use the current template and send
    if (!state.isAutomatedFollowup) {
      const from = state.previewTabs[0].from || null

      const massActionObject = {
        listId,
        type: 'survey',
        data: {
          surveyPath: state.surveyPath,
          UUIDPrefix: state.UUIDPrefix,
          decypherSubList: state.decypherSubList,
          UIDParameter: state.UIDParameter,
          baseLink: state.baseLink,
          body: prepTemplateForSending(state.template, from),
          surveyType: state.surveyType,
          additionalReplacements: state.additionalReplacements,
          templateType
        },
        actions: items.map(item => ({
          subject: state.previewTabs[state.selectedTabIndex].subject,
          surveyUUID: item.surveyUUID,
          uidPrefix: state.UUIDPrefix,
          listItemId: item.id,
          to: state.to[item.id].map(e => e.email).filter(x => x),
          cc: state.cc.map(e => e.email),
          bcc: state.bcc.map(e => e.email)
        }))
      }

      if (from) {
        massActionObject.from = from.email
      }

      await massProcessingApi
        .createMassAction(massActionObject)
        .then(massAction => {
          dispatch({
            type: actions.CREATE_MASS_ACTION_DONE,
            payload: massAction.id
          })
        })

      dispatch({
        type: actions.FEEDBACK,
        payload: 'Emails have been queueued up for processing'
      })
      dispatch({
        type: actions.NEXT_STAGE
      })
      return
    }
    // For automated follow ups starts
    const { previewTabs } = state

    const { staticData } = stores.staticData

    // Avoid executing getScheduledTime too many times, so placing it outside of previewTabs.map loops
    const experts = items.map(item => {
      const { address } = item.data
      let timezone = null
      if (address && address.timezoneId) {
        const { timezones } = staticData.names
        timezone = timezones[address.timezoneId]
      }
      return {
        ...item,
        scheduleSlots: getScheduledTime(
          timezone,
          state.automationFrequencyInMinutes,
          3 // Number of follow ups
        )
      }
    })

    // Generate a bunch of promises and use Promise.all
    // Each tab has a selectedTemplate, using that information to generate different massActions
    // Because one template can be sent to multiple experts, so multiple actions will be generated for one massAction
    const massActionPromises = previewTabs.map((tab, tabIndex) => {
      const subject = tab.subject
      const content = tab.selectedTemplate.content
      const cc = state.cc.map(e => e.email)
      const bcc = state.bcc.map(e => e.email)

      // Generate actions from each selected experts
      const actions = experts.map(expert => {
        // Base action
        const baseAction = {
          subject: subject,
          listItemId: expert.id,
          surveyUUID: expert.surveyUUID,
          uidPrefix: state.UUIDPrefix,
          to: state.to[expert.id].map(e => e.email).filter(i => i),
          cc,
          bcc
        }

        if (tab.isFollowup) {
          // To avoid follow up email being send to expert at the same time
          const hourIndex = tabIndex - 1

          baseAction.scheduleTime = expert.scheduleSlots[hourIndex]
        }

        return baseAction
      })

      const massAction = {
        listId,
        type: 'survey',
        data: {
          surveyPath: state.surveyPath,
          UUIDPrefix: state.UUIDPrefix,
          body: prepTemplateForSending(content, tab.from),
          additionalReplacements: state.additionalReplacements,
          templateType,
          baseLink: state.baseLink,
          surveyType: state.surveyType,
          decypherSubList: state.decypherSubList,
          UIDParameter: state.UIDParameter
        },
        actions: actions.map(a => ({
          ...a,
          followupIndex: tabIndex
        }))
      }

      // The first mass action is queue up right away
      if (tab.isFollowup) {
        // isAutomatedFollowup is the flag that backend uses to determine if massAction should be queued or scheduled
        massAction.isAutomatedFollowup = true
        massAction.data.followupIndex = tabIndex
      }

      // If from doesn't exist, backend default to the user's email
      if (tab.from?.email) {
        massAction.from = tab.from?.email
      }

      return massProcessingApi.createMassAction(massAction)
    })

    const results = await Promise.all(massActionPromises)
    dispatch({
      type: actions.CREATE_MULTIPLE_MASS_ACTION_DONE,
      payload: results
    })

    dispatch({
      type: actions.FEEDBACK,
      payload: 'Emails have been queued up for processing'
    })

    dispatch({
      type: actions.NEXT_STAGE
    })
  } catch (e) {
    console.error(e)
  }
}

function SurveyActionComponent({ massAction, items, list }) {
  const initialEmailState = getInitialState()
  const replacements = getAdditionalReplacements()

  const [state, setState] = React.useState({
    ...initialEmailState,
    isSurveyAction: true,
    finishAction: sendSurveys,
    loadingSurveys: true,
    surveyPath: '',
    template: null,
    UUIDPrefix: '',
    UIDParameter: 'UID',
    creatingUUIDs: false,
    additionalReplacements: {
      ...replacements,
      surveyLink: 'surveyLink'
    },
    additionalItemData: (i, s) => {
      return {
        surveyLink: generateSurveyLink(i, s),
        surveyPath: s.surveyPath
      }
    },
    surveyType: 'decypher',
    surveys: []
  })

  const initialLoad = async () => {
    const { initialEmailTemplate } = await getEmailTemplates('al_survey')

    const oldLocalTemplates = localStorage.getItem('al_email_templates')
    const oldLocalTemplatesParsed = oldLocalTemplates
      ? JSON.parse(oldLocalTemplates)
      : []

    // Local template currently don't make distinction between email groups, so passing in group name is only to make data consistent
    const formattedOldLocalTemplates = oldLocalTemplatesParsed.map(ot =>
      convertLocalTemplateToDBSchema(ot, 'al_survey')
    )

    const supervisor = await getSupervisorFromId()

    const newTabData = state.previewTabs.map((tab, index) => {
      const originalTab = {
        ...tab,
        selectedTemplate: initialEmailTemplate,
        // Concat both local storage templates with db templates
        templates: [initialEmailTemplate, ...formattedOldLocalTemplates],
        subject: initialEmailTemplate.subject
      }

      // If the tab is the last one, prefill supervisor email for "from" field
      if (state.previewTabs.length - 1 === index && supervisor) {
        const { currentPosition } = replacements
        // employeePositionId 1 is Associate
        if (currentPosition && currentPosition.employeePositionId === 1) {
          // Send as supervisor
          originalTab.from = supervisor
        }
      }
      return originalTab
    })

    const to = {}
    for (const item of items.filter(i => i.formatted?.emails?.length)) {
      to[item.id] = item.formatted.emails
        .map(email => (email.selected ? email : null))
        .filter(i => i)
    }

    setState({
      ...state,
      supervisor,
      previewTabs: newTabData,
      loading: false,
      // The template to display and edit
      template: newTabData[state.selectedTabIndex].selectedTemplate.content,
      subject: newTabData[state.selectedTabIndex].selectedTemplate.subject,
      to
    })
  }

  useEffect(() => {
    if ([0, 1].includes(massAction.stage)) {
      massProcessingApi.getSurveys().then(surveys => {
        setState(s => ({ ...s, loadingSurveys: false, surveys }))
      })
    }
  }, [massAction.stage, setState])

  // Get the email templates on initialization
  useEffect(() => {
    initialLoad()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  const currentStage = survey.stages[massAction.stage]

  // Load the component according to stage
  if (currentStage) {
    const { component: NestedComponent } = currentStage
    return (
      <NestedComponent
        items={items}
        list={list}
        state={state}
        setState={setState}
        action={survey}
      />
    )
  }
  // If massAction's stage is out of range of the stage array, dont render anything
  return null
}

/**
 * Render meeting selector
 *
 * @param {{ state: ReturnType<getInitialState>; setState: import('react').Dispatch<ReturnType<getInitialState>>; list: any}} param0
 */
function SurveySelector({ state, setState }) {
  const dispatch = React.useContext(ListViewDispatchContext)
  useEffect(() => {}, [])

  return !state.surveys.length ? (
    <CircularProgress
      style={{
        color: '#429bdc',
        width: '25px',
        height: '25px'
      }}
    />
  ) : (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
        <FormControl style={{ width: 150 }}>
          <InputLabel id="survey-select-type">Select survey type</InputLabel>
          <Select
            width={400}
            labelId="survey-select-type"
            placeholder={'Select survey'}
            onChange={e => setState({ ...state, surveyType: e.target.value })}
            label={'Select Survey Type'}
            value={state.surveyType}>
            <MenuItem value={state.surveyType}>Select a survey type</MenuItem>
            <MenuItem value={'decypher'}>Decypher</MenuItem>
            <MenuItem value={'owl'}>Owl</MenuItem>
            <MenuItem value={'other'}>Other</MenuItem>
          </Select>
        </FormControl>

        <Autocomplete
          style={{
            width: '200px',
            marginLeft: '10px'
          }}
          label={'Select Survey'}
          onChange={(_e, val) => {
            return setState({ ...state, surveyPath: val?.path || '' })
          }}
          options={state.surveys}
          getOptionLabel={option => option.name}
          renderInput={params => (
            <TextField
              {...params}
              style={{ height: '48px', marginTop: 0, marginBottom: 0 }}
              label="Select Survey"
              margin="normal"
            />
          )}
        />
        {state.surveyType === 'decypher' && (
          <TextField
            style={{ marginLeft: '10px' }}
            label="Decypher List"
            value={state.decypherSubList}
            onChange={e =>
              setState({ ...state, decypherSubList: e.target.value })
            }
          />
        )}

        {state.surveyType === 'decypher' && (
          <TextField
            style={{ marginLeft: '10px' }}
            label="Panel UUID parameter"
            value={state.UIDParameter}
            onChange={e => setState({ ...state, UIDParameter: e.target.value })}
          />
        )}

        {state.surveyType !== 'owl' && (
          <TextField
            style={{ marginLeft: '10px' }}
            label="UUID Prefix"
            value={state.UUIDPrefix}
            onChange={e => setState({ ...state, UUIDPrefix: e.target.value })}
          />
        )}

        {state.surveyType !== 'decypher' && (
          <TextField
            style={{ marginLeft: '10px' }}
            label="Survey link base"
            value={state.baseLink}
            onChange={e => setState({ ...state, baseLink: e.target.value })}
          />
        )}

        <span
          style={{ margin: '15px 0 0 10px' }}
          className={
            !!state.surveyPath.length
              ? `al-text-button`
              : 'al-text-button-disabled'
          }
          onClick={() =>
            !!state.surveyPath.length
              ? dispatch({
                  type: actions.NEXT_STAGE
                })
              : null
          }>
          Next
        </span>
        <span
          style={{ margin: '15px 0 0 10px' }}
          className={`al-text-button`}
          onClick={() => {
            dispatch({
              type: actions.END_ACTION
            })
          }}>
          Cancel
        </span>
      </div>
    </>
  )
}

function generateSurveyLink(_i, s) {
  // UUID for decypher
  const getUrl = () => {
    const uuidPart = `${s.UUIDPrefix}{{surveyUUID}}`

    const subListPart = s.decypherSubList ? `list=${s.decypherSubList}&` : ''

    if (s.surveyType === 'decypher') {
      return `https://emea.focusvision.com/survey/${
        s.surveyPath
      }?${subListPart}${
        !!s.UIDParameter.length ? s.UIDParameter : 'UID'
      }=${uuidPart}`
    } else if (s.surveyType === 'owl') {
      return '{{MP_LINK}}'
    } else if (s.surveyType === 'other') {
      return s.baseLink + uuidPart
    } else {
      throw new Error('Unknown survey type!')
    }
  }
  return `<a target="__blank" href=${getUrl()}>${getUrl()}</a>`
}
